import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Loader2,
  Printer,
  Check,
  AlertCircle,
  Clock,
  DollarSign,
  ShoppingCart,
  ChevronRight,
  Cloud,
  Monitor,
  ArrowLeft,
  Package,
  RotateCcw,
  Info,
  CheckCircle2,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import { ManagerPinDialog } from '@/components/ManagerPinDialog';
import {
  useRegisterSettlement,
  formatPaymentBreakdown,
} from '@/hooks/useRegisterSettlement';
import { printSettlementReport } from '@/components/settlement/SettlementPrintView';

// Jamaica Dollar denominations
const JMD_DENOMINATIONS = [
  { value: 50.00, label: '$50' },
  { value: 100.00, label: '$100' },
  { value: 500.00, label: '$500' },
  { value: 1000.00, label: '$1,000' },
  { value: 5000.00, label: '$5,000' },
];

interface ClosedSession {
  id: string;
  register_id: string | null;
  user_id: string | null;
  session_type: 'register' | 'virtual';
  opened_at: string;
  closed_at: string;
  settled_at: string | null;
  opening_balance: number;
  closing_balance: number | null;
  variance: number | null;
  cash_payments: number;
  register: { name: string } | null;
  user: { first_name: string | null; last_name: string | null } | null;
  settled_by_user: { first_name: string | null; last_name: string | null } | null;
}

export default function SettleRegister() {
  const { sessionId: paramSessionId } = useParams<{ sessionId?: string }>();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get('view') === 'true';
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const { profile } = useAuth();
  const { effectiveRole, isLoading: permissionsLoading } = usePermissions();
  const { formatCurrency } = useCurrency();

  // Check if user can settle registers (supervisor+ only)
  // Only check after permissions have loaded to prevent Access Denied flicker
  const canSettleRegister = !permissionsLoading && ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(effectiveRole || '');

  // Settlement hook
  const {
    openSessions,
    isLoadingSessions,
    fetchSettlementData,
    settleSession,
    refetchOpenSessions,
  } = useRegisterSettlement();

  // Fetch today's closed sessions
  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString();
  }, []);

  const { data: closedSessions, isLoading: isLoadingClosed, refetch: refetchClosedSessions } = useQuery({
    queryKey: ['closed-sessions-today', currentLocation?.id, todayStart],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      const { data, error } = await supabase
        .from('cash_drawer_sessions')
        .select(`
          id,
          register_id,
          user_id,
          session_type,
          opened_at,
          closed_at,
          settled_at,
          opening_balance,
          closing_balance,
          variance,
          cash_payments,
          register:registers(name),
          user:profiles!cash_drawer_sessions_user_id_fkey(first_name, last_name),
          settled_by_user:profiles!cash_drawer_sessions_settled_by_fkey(first_name, last_name)
        `)
        .eq('location_id', currentLocation.id)
        .eq('status', 'closed')
        .gte('closed_at', todayStart)
        .order('closed_at', { ascending: false });

      if (error) throw error;

      // Map the data to match expected interface (Supabase returns arrays for single joins)
      return (data || []).map((row: any) => ({
        id: row.id,
        register_id: row.register_id,
        user_id: row.user_id,
        session_type: row.session_type || 'register',
        opened_at: row.opened_at,
        closed_at: row.closed_at,
        settled_at: row.settled_at,
        opening_balance: row.opening_balance,
        closing_balance: row.closing_balance,
        variance: row.variance,
        cash_payments: row.cash_payments,
        register: row.register,
        user: row.user,
        settled_by_user: row.settled_by_user,
      })) as ClosedSession[];
    },
    enabled: !!currentLocation?.id,
  });

  // Reprint state
  const [reprintingId, setReprintingId] = useState<string | null>(null);

  // Selected session for settlement or viewing
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(paramSessionId || null);
  const [isViewingClosedSession, setIsViewingClosedSession] = useState(isViewMode && !!paramSessionId);

  // Set view mode when navigating with ?view=true query param
  useEffect(() => {
    if (paramSessionId && isViewMode) {
      setSelectedSessionId(paramSessionId);
      setIsViewingClosedSession(true);
    }
  }, [paramSessionId, isViewMode]);

  // Denomination quantities for cash counting
  const [denominations, setDenominations] = useState<Record<string, number>>({});
  const [cashCounted, setCashCounted] = useState<string>('');

  // Settlement state
  const [isSettling, setIsSettling] = useState(false);
  const [showSettlementPinDialog, setShowSettlementPinDialog] = useState(false);

  // Fetch settlement data for selected session
  const { data: settlementData, isLoading: settlementLoading } = useQuery({
    queryKey: ['settlement-data', selectedSessionId],
    queryFn: () => selectedSessionId ? fetchSettlementData(selectedSessionId) : null,
    enabled: !!selectedSessionId,
    staleTime: 1000 * 10,
    refetchInterval: 1000 * 30, // Refresh every 30 seconds
  });

  // Format payment breakdown for display
  const paymentBreakdownFormatted = useMemo(() => {
    if (!settlementData?.payment_breakdown) return [];
    return formatPaymentBreakdown(settlementData.payment_breakdown);
  }, [settlementData?.payment_breakdown]);

  // Calculate cash totals
  const cashTotal = useMemo(() => {
    return Object.entries(denominations).reduce((sum, [value, qty]) => {
      return sum + parseFloat(value) * qty;
    }, 0);
  }, [denominations]);

  const expectedCash = settlementData?.expected_balance ?? (
    (settlementData?.opening_balance || 0) +
    (settlementData?.cash_payments || 0) -
    (settlementData?.cash_refunds || 0) +
    (settlementData?.pay_ins || 0) -
    (settlementData?.pay_outs || 0)
  );

  const countedCash = parseFloat(cashCounted) || cashTotal;
  const cashDifference = countedCash - expectedCash;

  // Update denomination quantity
  const updateDenomination = (value: string, qty: number) => {
    const newDenominations = {
      ...denominations,
      [value]: Math.max(0, qty),
    };
    setDenominations(newDenominations);
    
    // Auto-update cash counted based on denominations
    const total = Object.entries(newDenominations).reduce((sum, [v, q]) => {
      return sum + parseFloat(v) * q;
    }, 0);
    setCashCounted(total.toFixed(2));
  };

  // Handle settlement approval
  const handleSettleClick = () => {
    if (!canSettleRegister) {
      toast.error('Permission Denied', {
        description: 'Only supervisors and managers can settle registers',
      });
      return;
    }
    setShowSettlementPinDialog(true);
  };

  // Process settlement after PIN approval
  const handleSettlementApproved = async (managerId: string, managerName: string) => {
    setShowSettlementPinDialog(false);
    
    if (!selectedSessionId || !settlementData) {
      toast.error('Error', {
        description: 'No session selected for settlement',
      });
      return;
    }

    setIsSettling(true);
    try {
      await settleSession.mutateAsync({
        sessionId: selectedSessionId,
        closingBalance: countedCash,
        denominations,
        approvedBy: managerId,
      });

      // Print settlement report
      printSettlementReport({
        data: {
          ...settlementData,
          closing_balance: countedCash,
          variance: cashDifference,
        },
        settledByName: managerName,
        formatCurrency,
      });

      toast.success('Register Settled', {
        description: 'Register has been settled successfully. Settlement report printed.',
      });

      // Clear selection and refresh
      setSelectedSessionId(null);
      setDenominations({});
      setCashCounted('');
      refetchOpenSessions();
      refetchClosedSessions();

    } catch (error: any) {
      toast.error('Settlement Failed', {
        description: error.message || 'Failed to settle register',
      });
    } finally {
      setIsSettling(false);
    }
  };

  // Print preview
  const handlePrintPreview = () => {
    if (!settlementData) return;

    printSettlementReport({
      data: {
        ...settlementData,
        closing_balance: countedCash,
        variance: cashDifference,
      },
      settledByName: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Unknown',
      formatCurrency,
    });
  };

  // Reprint settlement report for closed session
  const handleReprintSettlement = async (sessionId: string) => {
    setReprintingId(sessionId);

    try {
      const settlementData = await fetchSettlementData(sessionId);

      if (!settlementData) {
        toast.error('Reprint Failed', {
        description: 'Could not load settlement data for this session',
      });
        return;
      }

      const settledByName = profile
        ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
        : 'Unknown';

      printSettlementReport({
        data: settlementData,
        settledByName,
        formatCurrency,
      });

      toast.success('Settlement Report Reprinted', {
        description: 'The settlement report has been sent to the printer',
      });
    } catch (error) {
      console.error('Failed to reprint settlement:', error);
      toast.error('Reprint Failed', {
        description: 'An error occurred while reprinting the settlement report',
      });
    } finally {
      setReprintingId(null);
    }
  };

  // Format time for display
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDuration = (openedAt: string) => {
    const start = new Date(openedAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Show loading while permissions are loading to prevent Access Denied flicker
  if (permissionsLoading) {
    return (
      <DashboardLayout
        header={
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-display font-semibold tracking-tight">
              Settle Registers
            </h1>
          </div>
        }
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  // Permission check - only shown after permissions have loaded
  if (!canSettleRegister) {
    return (
      <DashboardLayout
        header={
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-display font-semibold tracking-tight">
              Settle Registers
            </h1>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle>Access Denied</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                Only supervisors, managers, and administrators can settle registers.
              </p>
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Location check
  if (!currentLocation) {
    return (
      <DashboardLayout
        header={
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-display font-semibold tracking-tight">
              Settle Registers
            </h1>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <CardTitle>Select a Location</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground">
                Please select a location to view open register sessions.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const handleBackClick = () => {
    setSelectedSessionId(null);
    setIsViewingClosedSession(false);
  };

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        {selectedSessionId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackClick}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        )}
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            {selectedSessionId
              ? (isViewingClosedSession ? 'Settlement Details' : 'Settle Register')
              : 'Settle Registers'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {currentLocation.name} • {selectedSessionId
              ? (isViewingClosedSession ? 'View settlement report' : 'Count and settle')
              : 'Manage register sessions'}
          </p>
        </div>
      </div>
      {selectedSessionId && settlementData && !isViewingClosedSession && (
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrintPreview} className="gap-2">
            <Printer className="h-4 w-4" />
            Print Preview
          </Button>
          <Button
            onClick={handleSettleClick}
            disabled={isSettling}
            className="gap-2"
          >
            {isSettling ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Settle Register
          </Button>
        </div>
      )}
      {selectedSessionId && settlementData && isViewingClosedSession && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleReprintSettlement(selectedSessionId)}
            disabled={reprintingId === selectedSessionId}
            className="gap-2"
          >
            {reprintingId === selectedSessionId ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Printer className="h-4 w-4" />
            )}
            Reprint Settlement
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout header={header}>
      {!selectedSessionId ? (
        // Session List View
        <div className="space-y-6">
          {(isLoadingSessions || isLoadingClosed) ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Open Sessions Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                    <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-lg font-semibold">Open Sessions</h2>
                  <Badge variant="secondary" className="ml-auto">
                    {openSessions.length} open
                  </Badge>
                </div>

                {openSessions.length === 0 ? (
                  <Card>
                    <CardContent className="py-8">
                      <div className="text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                          <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
                        </div>
                        <h3 className="font-medium mb-1">All Registers Settled</h3>
                        <p className="text-sm text-muted-foreground">
                          There are no open register sessions at this location.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {openSessions.map((session) => (
                      <Card
                        key={session.id}
                        className="cursor-pointer hover:border-primary/50 transition-colors border-amber-200 dark:border-amber-800/50"
                        onClick={() => setSelectedSessionId(session.id)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              {session.session_type === 'virtual' ? (
                                <Cloud className="h-5 w-5 text-blue-500" />
                              ) : (
                                <Monitor className="h-5 w-5 text-green-500" />
                              )}
                              <div>
                                <CardTitle className="text-base">{session.register_name}</CardTitle>
                                <CardDescription>{session.user_name}</CardDescription>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400">
                              Open
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span>{formatTime(session.opened_at)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span>{formatDuration(session.opened_at)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                              <span>{session.order_count} orders</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              <span>{formatCurrency(session.total_sales)}</span>
                            </div>
                          </div>
                          {(session.void_count > 0) && (
                            <div className="text-xs text-amber-600 dark:text-amber-400">
                              {session.void_count} void(s) - {formatCurrency(session.void_amount)}
                            </div>
                          )}
                          <div className="flex items-center justify-end text-sm text-muted-foreground">
                            <span>Click to settle</span>
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Closed Sessions Section (Today Only) */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-lg font-semibold">Settled Today</h2>
                  <Badge variant="secondary" className="ml-auto">
                    {closedSessions?.length || 0} settled
                  </Badge>
                </div>

                <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-800 dark:bg-blue-950/50">
                  <Info className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <p className="text-blue-800 dark:text-blue-200">
                    Only showing sessions settled today. For historical settlements, visit{' '}
                    <Button
                      variant="link"
                      className="h-auto p-0 text-blue-700 dark:text-blue-300 underline"
                      onClick={() => navigate('/reports/register-closures')}
                    >
                      Reports &gt; Register Closures
                    </Button>
                  </p>
                </div>

                {(!closedSessions || closedSessions.length === 0) ? (
                  <Card>
                    <CardContent className="py-8">
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">
                          No sessions have been settled today.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {closedSessions.map((session) => {
                      const variance = session.variance ?? 0;
                      const hasVariance = Math.abs(variance) > 0.01;
                      const userName = session.user
                        ? `${session.user.first_name || ''} ${session.user.last_name || ''}`.trim()
                        : 'Unknown';
                      const settledByName = session.settled_by_user
                        ? `${session.settled_by_user.first_name || ''} ${session.settled_by_user.last_name || ''}`.trim()
                        : null;

                      return (
                        <Card
                          key={session.id}
                          className="cursor-pointer hover:border-green-400 dark:hover:border-green-600 transition-colors border-green-200 dark:border-green-800/50"
                          onClick={() => {
                            setSelectedSessionId(session.id);
                            setIsViewingClosedSession(true);
                          }}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                {session.session_type === 'virtual' ? (
                                  <Cloud className="h-5 w-5 text-blue-500" />
                                ) : (
                                  <Monitor className="h-5 w-5 text-muted-foreground" />
                                )}
                                <div>
                                  <CardTitle className="text-base">
                                    {session.register?.name || (session.session_type === 'virtual' ? 'Virtual Session' : 'Unknown')}
                                  </CardTitle>
                                  <CardDescription>{userName}</CardDescription>
                                </div>
                              </div>
                              <Badge variant="outline" className="text-xs border-green-300 text-green-700 dark:border-green-700 dark:text-green-400">
                                Settled
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Opened</span>
                                <p className="font-medium">{formatTime(session.opened_at)}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Closed</span>
                                <p className="font-medium">{formatTime(session.closed_at)}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Cash</span>
                                <p className="font-medium font-mono">{formatCurrency(session.cash_payments)}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Variance</span>
                                <p className={cn(
                                  "font-medium font-mono",
                                  hasVariance && variance > 0 && "text-green-600",
                                  hasVariance && variance < 0 && "text-red-600"
                                )}>
                                  {hasVariance ? (variance > 0 ? '+' : '') + formatCurrency(variance) : '-'}
                                </p>
                              </div>
                            </div>
                            {settledByName && session.settled_at && (
                              <div className="text-xs text-muted-foreground pt-2 border-t">
                                Settled by {settledByName} at {formatTime(session.settled_at)}
                              </div>
                            )}
                            <div className="flex items-center justify-between pt-2">
                              <span className="text-xs text-muted-foreground">Click to view details</span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReprintSettlement(session.id);
                                }}
                                disabled={reprintingId === session.id}
                              >
                                {reprintingId === session.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="h-4 w-4" />
                                )}
                                Reprint
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        // Settlement Detail View
        <div className="space-y-6">
          {settlementLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !settlementData ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Session not found or already settled.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Left Column - Session Info & Cash Counting */}
              <div className="lg:col-span-2 space-y-6">
                {/* Session Info */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Session Details</CardTitle>
                      <div className="flex items-center gap-2">
                        {isViewingClosedSession && (
                          <Badge variant="outline" className="text-xs border-green-300 text-green-700 dark:border-green-700 dark:text-green-400">
                            Settled
                          </Badge>
                        )}
                        {settlementData.session_type === 'virtual' && (
                          <Badge variant="secondary" className="gap-1">
                            <Cloud className="h-3 w-3" />
                            Virtual Session
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className={cn(
                      "grid gap-4",
                      isViewingClosedSession ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-4"
                    )}>
                      <div>
                        <span className="text-sm text-muted-foreground">Register</span>
                        <p className="font-medium">{settlementData.register_name}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Cashier</span>
                        <p className="font-medium">{settlementData.cashier_name}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Opened</span>
                        <p className="font-medium">{formatTime(settlementData.opened_at)}</p>
                      </div>
                      {isViewingClosedSession && settlementData.closed_at ? (
                        <div>
                          <span className="text-sm text-muted-foreground">Closed</span>
                          <p className="font-medium">{formatTime(settlementData.closed_at)}</p>
                        </div>
                      ) : (
                        <div>
                          <span className="text-sm text-muted-foreground">Duration</span>
                          <p className="font-medium">{formatDuration(settlementData.opened_at)}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Cash Count - Only show for open sessions */}
                {!isViewingClosedSession && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Quick Cash Count</CardTitle>
                      <CardDescription>Enter denomination quantities for fast counting</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-5 gap-3 mb-4">
                        {JMD_DENOMINATIONS.map((denom) => {
                          const qty = denominations[denom.value.toString()] || 0;
                          return (
                            <div key={denom.value} className="text-center">
                              <div className="text-xs text-muted-foreground mb-1">{denom.label}</div>
                              <Input
                                type="number"
                                min="0"
                                value={qty || ''}
                                onChange={(e) => updateDenomination(
                                  denom.value.toString(),
                                  parseInt(e.target.value) || 0
                                )}
                                className="text-center h-10"
                                placeholder="0"
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div>
                          <span className="text-sm text-muted-foreground">Or enter total directly:</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input
                            type="number"
                            step="0.01"
                            value={cashCounted}
                            onChange={(e) => setCashCounted(e.target.value)}
                            className="w-32 text-right font-mono"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Payment Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Payment Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {paymentBreakdownFormatted.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No payments recorded
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {paymentBreakdownFormatted.map((method) => (
                          <div key={method.method}>
                            <div className="flex justify-between font-medium mb-1">
                              <span>{method.methodLabel}</span>
                              <span className="font-mono">{formatCurrency(method.total)}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mb-2">
                              {method.count} transaction{method.count !== 1 ? 's' : ''}
                            </div>
                            {method.types.length > 0 && (
                              <div className="pl-3 space-y-1 border-l-2 border-muted">
                                {method.types.map((type, idx) => (
                                  <div key={type.type_id || idx} className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{type.type_name}</span>
                                    <span className="font-mono">{formatCurrency(type.total)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Cash Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle>Cash Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Opening Float</span>
                        <span className="font-mono">{formatCurrency(settlementData.opening_balance)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">+ Cash Sales</span>
                        <span className="font-mono">{formatCurrency(settlementData.cash_payments)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">- Cash Refunds</span>
                        <span className="font-mono">{formatCurrency(settlementData.cash_refunds)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">+ Pay-Ins</span>
                        <span className="font-mono">{formatCurrency(settlementData.pay_ins)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">- Pay-Outs</span>
                        <span className="font-mono">{formatCurrency(settlementData.pay_outs)}</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t font-semibold">
                        <span>Expected Cash</span>
                        <span className="font-mono">{formatCurrency(isViewingClosedSession ? settlementData.expected_balance : expectedCash)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Counted Cash</span>
                        <span className="font-mono">{formatCurrency(isViewingClosedSession ? settlementData.closing_balance : countedCash)}</span>
                      </div>
                      {(() => {
                        const displayVariance = isViewingClosedSession ? settlementData.variance : cashDifference;
                        const hasVariance = Math.abs(displayVariance) > 0.01;
                        return (
                          <div className={cn(
                            "flex justify-between pt-3 border-t font-semibold",
                            hasVariance && displayVariance > 0 && "text-green-600",
                            hasVariance && displayVariance < 0 && "text-red-600"
                          )}>
                            <span>Variance</span>
                            <span className="font-mono">
                              {hasVariance ? (displayVariance > 0 ? '+' : '') : ''}
                              {formatCurrency(displayVariance)}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Sales & Items Sold */}
              <div className="space-y-6">
                {/* Sales Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle>Sales Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Orders</span>
                        <span className="font-semibold">{settlementData.total_orders}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Completed</span>
                        <span className="font-semibold">{settlementData.completed_orders}</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t">
                        <span className="text-muted-foreground">Gross Sales</span>
                        <span className="font-mono">{formatCurrency(settlementData.gross_sales)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">- Discounts</span>
                        <span className="font-mono">{formatCurrency(settlementData.discount_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">- Voids</span>
                        <span className="font-mono">{formatCurrency(settlementData.void_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">- Refunds</span>
                        <span className="font-mono">{formatCurrency(settlementData.refund_amount)}</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t font-semibold">
                        <span>Net Sales</span>
                        <span className="font-mono">{formatCurrency(settlementData.net_sales)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax Collected</span>
                        <span className="font-mono">{formatCurrency(settlementData.tax_collected)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Items Sold Summary */}
                {settlementData.items_sold_summary && settlementData.items_sold_summary.length > 0 && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-muted-foreground" />
                        <CardTitle>Items Sold</CardTitle>
                      </div>
                      <CardDescription>
                        {settlementData.items_sold_summary.reduce((sum, item) => sum + item.quantity, 0)} items total
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* Header row */}
                      <div className="flex items-center py-2 border-b-2 border-muted text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        <span className="w-14 text-right pr-4">Qty</span>
                        <span className="flex-1">Item</span>
                        <span className="w-24 text-right">Total</span>
                      </div>
                      {/* Items list */}
                      <div className="max-h-64 overflow-y-auto">
                        {settlementData.items_sold_summary.map((item, idx) => (
                          <div key={idx} className="flex items-center py-2 border-b border-muted last:border-0">
                            <span className="w-14 text-right pr-4 font-mono text-sm font-semibold">
                              {item.quantity}
                            </span>
                            <span className="flex-1 text-sm font-medium truncate">
                              {item.name}
                            </span>
                            <span className="w-24 text-right font-mono text-sm">
                              {formatCurrency(item.total)}
                            </span>
                          </div>
                        ))}
                      </div>
                      {/* Total row */}
                      <div className="flex items-center py-2 border-t-2 border-muted mt-2 font-semibold">
                        <span className="w-14 text-right pr-4 font-mono text-sm">
                          {settlementData.items_sold_summary.reduce((sum, item) => sum + item.quantity, 0)}
                        </span>
                        <span className="flex-1 text-sm">Total</span>
                        <span className="w-24 text-right font-mono text-sm">
                          {formatCurrency(settlementData.items_sold_summary.reduce((sum, item) => sum + item.total, 0))}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Voids & Refunds */}
                {(settlementData.void_count > 0 || settlementData.refund_count > 0) && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Voids & Refunds</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {settlementData.void_count > 0 && (
                          <div>
                            <div className="flex justify-between text-amber-600 dark:text-amber-400 font-medium">
                              <span>Voids ({settlementData.void_count})</span>
                              <span className="font-mono">{formatCurrency(settlementData.void_amount)}</span>
                            </div>
                            {settlementData.voids_summary.length > 0 && (
                              <div className="mt-1 space-y-1">
                                {settlementData.voids_summary.slice(0, 5).map((v, i) => (
                                  <div key={i} className="text-xs text-muted-foreground">
                                    #{v.order_number} - {formatCurrency(v.amount)} - {v.reason}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {settlementData.refund_count > 0 && (
                          <div>
                            <div className="flex justify-between text-red-600 dark:text-red-400 font-medium">
                              <span>Refunds ({settlementData.refund_count})</span>
                              <span className="font-mono">{formatCurrency(settlementData.refund_amount)}</span>
                            </div>
                            {settlementData.refunds_summary.length > 0 && (
                              <div className="mt-1 space-y-1">
                                {settlementData.refunds_summary.slice(0, 5).map((r, i) => (
                                  <div key={i} className="text-xs text-muted-foreground">
                                    #{r.order_number} - {formatCurrency(r.amount)} - {r.reason}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manager PIN Dialog */}
      <ManagerPinDialog
        open={showSettlementPinDialog}
        onOpenChange={setShowSettlementPinDialog}
        onVerified={handleSettlementApproved}
        title="Settlement Approval Required"
        description="Enter manager PIN to approve this register settlement"
      />
    </DashboardLayout>
  );
}

